/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Box, Text, IntrinsicBox, Link } from '@tishman/components';
import React, { Fragment, useMemo } from 'react';

import { putInArray } from '../../../services/viva/utils';
import {
  CHALET_AK,
  TREE_PHOTO_PRODUCT_AK,
} from '../../../services/viva/constants';

import type { OrderResponse } from '../../../services/viva/types/response/order';

interface PrintInstructionsProps {
  order: OrderResponse;
  productAK?: string;
}

export const PrintInstructions = (
  props: PrintInstructionsProps,
): React.JSX.Element => {
  const { order, productAK } = props;

  const seller = order ? order.SALE.AK.split('.')[0] : 'TOR';
  const resyLink = useMemo(() => {
    const itemListItem = putInArray(order.ITEMLIST.ITEM);
    const jupiterItem = itemListItem.find((item) =>
      item.PRODUCT.NAME.includes('Jupiter'),
    );

    if (!jupiterItem) {
      return 'https://resy.com/cities/ny/jupiter';
    }

    const urlParams = {
      qty: jupiterItem.INFO.QTY,
      date: jupiterItem.ITEMPERFORMANCELIST.ITEMPERFORMANCE.DATETIME.split(
        'T',
      )[0],
    };

    return `https://resy.com/cities/ny/jupiter?seats=${urlParams.qty}&date=${urlParams.date}`;
  }, [order.ITEMLIST.ITEM]);

  return (
    <Box
      sx={{
        'p': [2, 4, 5],
        'pt': [2, 4],
        'borderTop': 'dashed 1px #333',
        '@media print': {
          pt: 3,
          px: 3,
          border: 'none',
        },
      }}
    >
      <Box
        sx={{
          '@media print': {
            width: '100%',
          },
        }}
      >
        <Text
          sx={{
            'mb': [4, 4],
            'pb': [4, 4],
            'borderBottom': '1px solid #000',
            '@media print': { fontSize: 3, mb: 2, pb: 2 },
          }}
          variant="mediumTitle"
        >
          Arrival instructions
        </Text>
        {productAK === CHALET_AK && (
          <Fragment>
            <Text variant="ticketArrivalInstructionsSubHeading">
              Holiday Chalets
            </Text>
            <Text variant="ticketArrivalInstructions">
              Please redeem at the Chalet entrance on 49th street and
              Rockefeller Plaza (northside of 49th street, next to the Tree).
            </Text>
          </Fragment>
        )}
        {productAK === TREE_PHOTO_PRODUCT_AK && (
          <Fragment>
            <Text variant="ticketArrivalInstructionsSubHeading">
              The Official Christmas Tree Photo
            </Text>
            <Text variant="ticketArrivalInstructions">
              Please redeem at the Tree Photo entrance located on 49th street
              between 5th and 6th Avenues (next to Kate Spade).
            </Text>
          </Fragment>
        )}
        <IntrinsicBox ratio={735 / 932}>
          {seller === 'RRC' && (
            <Fragment>
              <Text variant="ticketArrivalInstructionsSubHeading">
                Rink directions:
              </Text>
              <Text variant="ticketArrivalInstructions">
                The main entrance to The Rink is located on 49th street between
                5th and 6th Avenues (next to Kate Spade). There is also a
                secondary, ADA accessible, entrance, on The Rink level at the
                southeast side of the plaza through The Rink Shop.
              </Text>
            </Fragment>
          )}
          {seller !== 'RRC' && (
            <Fragment>
              <Text variant="ticketArrivalInstructionsSubHeading">
                Top of the Rock, All-In Pass, Rockefeller Center Tour, and Rock
                Pass:
              </Text>
              <Text variant="ticketArrivalInstructions">
                The entrance to Top of the Rock is located on 50th Street
                between 5th and 6th Avenues.
              </Text>
              <Text variant="ticketArrivalInstructionsSubHeading">
                The Beam Experience and Skylift:
              </Text>
              <Text variant="ticketArrivalInstructions">
                Present your ticket QR code on your mobile device at the kiosk
                near the entrance of The Beam Experience (Level 2-69th floor) or
                Skylift (Level 3-70th floor) once you arrive at Top of the Rock
                Observation Deck.
              </Text>
              <Text variant="ticketArrivalInstructionsSubHeading">
                3 Course Meal at Jupiter & 5 Acres
              </Text>
              <Text variant="ticketArrivalInstructions">
                <Text variant="strong">RESERVATION IS REQUIRED.</Text> You will
                receive an email from OpenTable confirming your reserved time.
                Your reservation will be booked approximately within 2 hours of
                your Top of the Rock or Rink reservation. You will be able to
                adjust your reservation time via OpenTable. Upon arrival, please
                present your QR code on your mobile device before being seated.
              </Text>
              <Text variant="ticketArrivalInstructionsSubHeading">
                VIP Rock Pass:
              </Text>
              <Text variant="ticketArrivalInstructions">
                Enter at the The Lounge located at 52 West 50th Street between
                5th and 6th Avenues, adjacent to the main Top of the Rock
                entrance.
              </Text>
            </Fragment>
          )}
          <Text variant="ticketArrivalInstructions">
            *Please note, there is no need to arrive early or queue before your
            designated ticket time.
          </Text>
          <Text variant="ticketArrivalInstructions">
            Upon arrival, present your ticket QR code on your mobile device.
          </Text>
        </IntrinsicBox>
      </Box>
    </Box>
  );
};
